import type { StatusTone } from '@beonit/adaptive-raindrop';
import { ApiError, ApiErrorCode } from '@shared/infrastructure/http/api-error.js';
import { feedbackFor } from '../feedback.js';

/**
 * The decisions a live connection to Virtual Profiler makes, with no connection in sight.
 *
 * Everything here is a function of its arguments: no socket, no timer, no `Date.now()`. The
 * wiring lives in `realtime-link.ts` and the Vue glue in `use-queue-realtime.ts`, and both are
 * deliberately thin — what an eye cannot check by looking at a screen is the retry schedule, so
 * the retry schedule is a pure function with its own spec.
 *
 * ## The rule that shapes the whole file
 *
 * A ticket is **single use**. socket.io reconnects on its own, and its own reconnection would
 * present the ticket it already spent — so every attempt has to start by minting a new one. That
 * is why `reconnection` is off on the socket and the retry loop is ours, and why the backoff is
 * written down here instead of being left to the library's defaults.
 */

/**
 * The events that mean something changed. Virtual Profiler's names, not ours.
 *
 * **Only the run change.** `analysis-queue-tick` is deliberately absent: it is emitted on every
 * dispatcher cycle — every three seconds, whether anything moved or not — so invalidating on it
 * would refetch the queue every three seconds forever. With the queue read model taking several
 * seconds to answer, requests start before the previous one has returned and pile up until they
 * time out. That is strictly worse than the polling this replaced, and it is what a heartbeat
 * mistaken for a change event buys you.
 *
 * The tick still arrives and is still useful: the socket receiving it is the proof the connection
 * is alive. It just does not make anybody ask a question.
 */
export const QUEUE_REALTIME_EVENTS = ['analysis-jobs-changed'] as const;

/**
 * Received, but never a reason to refetch.
 *
 * Listened to separately so the heartbeat can keep the connection honest without touching the
 * cache. Naming it here is also what stops somebody adding it back to the list above.
 */
export const QUEUE_HEARTBEAT_EVENTS = ['analysis-queue-tick'] as const;

/**
 * How long a burst of events is collapsed into one invalidation.
 *
 * A batch of fifty videos lands as fifty events in a few milliseconds. Fifty refetches of the
 * queue read model would be fifty requests answering nearly the same thing.
 */
export const REALTIME_BURST_MS = 250;

/** First wait after a connection is lost, then doubling. */
export const FIRST_RETRY_MS = 1_000;

/**
 * The ceiling of the backoff.
 *
 * Reached and then held for as long as the outage lasts: there is no attempt limit on purpose.
 * The gateway in front of Virtual Profiler cuts idle connections at 3600 s, so a session left
 * open all morning **will** be dropped, and it has to come back without anybody clicking
 * anything.
 */
export const MAX_RETRY_MS = 30_000;

/** What the screen says about itself. Four states, no more. */
export type RealtimeStatus = 'connecting' | 'live' | 'retrying' | 'error';

export type ReconnectionPlan =
  | { readonly action: 'retry'; readonly delayMs: number }
  /** No further attempt will help: only a new session or new permissions would. */
  | { readonly action: 'give-up' };

/**
 * Exponential backoff, capped.
 *
 * `failures` is the count of consecutive failures, so the first one waits `FIRST_RETRY_MS`. A
 * count of zero would mean «nothing failed», which is not a question this answers; it is
 * clamped rather than trusted, because an off-by-one here turns a reconnection into a request
 * flood against a service that is already having a bad day.
 */
export function retryDelayMs(failures: number): number {
  const exponent = Math.max(1, Math.floor(failures)) - 1;

  return Math.min(MAX_RETRY_MS, FIRST_RETRY_MS * 2 ** exponent);
}

/**
 * Whether retrying is pointless.
 *
 * Only two causes are hopeless, and both are about who is asking rather than about the network:
 * the session is gone, or the permission is. Everything else — no network, Virtual Profiler
 * down, a refused or expired ticket, the gateway's idle cut — heals by trying again with a
 * fresh ticket, which is the ordinary case this connection is built around.
 */
function isHopeless(cause: unknown): boolean {
  if (!(cause instanceof ApiError)) return false;

  return (
    cause.is(ApiErrorCode.PERMISSION_DENIED) ||
    cause.is(ApiErrorCode.UNAUTHENTICATED) ||
    cause.status === 401 ||
    cause.status === 403
  );
}

export function planReconnection(failures: number, cause: unknown): ReconnectionPlan {
  if (isHopeless(cause)) return { action: 'give-up' };

  return { action: 'retry', delayMs: retryDelayMs(failures) };
}

/**
 * What a person reads about a failed connection.
 *
 * Chosen by error **code** for anything that came through the gateway, which is what
 * `feedbackFor` already does for every other screen of the module. A socket-level failure has
 * no code of ours — its text is socket.io's, in English, and says «xhr poll error» — so it gets
 * one sentence of our own instead of being shown raw.
 */
export function describeRealtimeFailure(cause: unknown): string {
  if (cause instanceof ApiError) return feedbackFor(cause);

  return 'No se ha podido mantener la conexión en directo con Virtual Profiler.';
}

export interface RealtimeAlarm {
  title: string;
  body: string;
  /** `warning` is «esto se está arreglando solo»; `danger` is «esto no se va a arreglar solo». */
  severity: 'warning' | 'danger';
}

export interface RealtimeStatusPresentation {
  /** The quiet badge in the header. */
  label: string;
  tone: StatusTone;
  /** Null when there is nothing to say out loud. */
  alarm: RealtimeAlarm | null;
}

/**
 * How the connection reads on screen.
 *
 * Two decisions worth defending:
 *
 * - **A broken connection is said out loud, not only in the badge.** A queue that stopped
 *   receiving events looks exactly like a queue with nothing in it, and that is the one
 *   confusion this screen cannot afford. The cost is that a reconnection lasting a second
 *   flashes an alarm; being briefly noisy beats being quietly wrong.
 * - **The words never name the fallback interval.** Polling is still there underneath as a
 *   safety net, and saying «se actualiza cada minuto» would become a lie the day somebody
 *   changes the constant. It says the screen keeps refreshing, more slowly, which stays true.
 * - **A connected socket is not a working screen.** The socket goes to Virtual Profiler and the
 *   data comes through the back-office, so they fail independently. Reporting «conectado» while
 *   the queue cannot be read is the worst of the three states: it tells somebody looking at an
 *   empty screen that everything is fine. `dataFailing` is what stops that.
 */
export function describeRealtimeStatus(
  status: RealtimeStatus,
  error: string | null,
  dataFailing = false,
): RealtimeStatusPresentation {
  // Before the switch: whatever the socket is doing, a screen that cannot read the queue is not
  // reporting good news. The socket's own alarm, if any, would be the less useful of the two.
  if (dataFailing) {
    return {
      label: 'Desconectado',
      tone: 'danger',
      alarm: {
        title: 'No se puede leer la cola',
        body: 'La conexión en directo puede estar bien, pero Virtual Profiler no está respondiendo a la lectura de la cola. Lo que se ve abajo está vacío porque no ha llegado, no porque no haya nada.',
        severity: 'danger',
      },
    };
  }

  switch (status) {
    case 'live':
      return { label: 'Conectado', tone: 'success', alarm: null };

    // No alarm: nobody needs telling that a screen they just opened is still opening.
    case 'connecting':
      return { label: 'Conectando…', tone: 'neutral', alarm: null };

    case 'retrying':
      return {
        label: 'Reconectando…',
        tone: 'warning',
        alarm: {
          title: 'Sin conexión en directo',
          body: `${error ?? 'Se ha perdido la conexión con Virtual Profiler.'} Se está reintentando solo. Mientras tanto la pantalla sigue actualizándose, pero más despacio: puede ir por detrás de lo que está pasando.`,
          severity: 'warning',
        },
      };

    case 'error':
      return {
        label: 'Sin conexión',
        tone: 'danger',
        alarm: {
          title: 'Sin conexión en directo',
          body: `${error ?? 'No se ha podido conectar con Virtual Profiler.'} Reintentarlo no va a servir: vuelve a entrar y recarga la pantalla.`,
          severity: 'danger',
        },
      };
  }
}
